import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { sessionApi, type SocialProvider } from "../../api/account";
import { storeApi } from "../../api/cart";
import { useSocialSignIn } from "../../lib/socialSignIn";
import { errorMessage, useSetSession } from "./session";

/** The store's settings, including which ways in this server offers. */
export function useStore() {
  return useQuery({ queryKey: ["store"], queryFn: storeApi.get, staleTime: Infinity });
}

/**
 * Google and Facebook: the provider's popup, then our server, which checks the token with
 * the provider and signs in, linking or creating the account (design doc §6.1).
 */
export function useSocialSession(returnTo: string) {
  const store = useStore();
  const social = useSocialSignIn(store.data?.sign_in);
  const setSession = useSetSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<SocialProvider | null>(null);
  const [error, setError] = useState<string>();

  const choose = (provider: SocialProvider) => {
    setError(undefined);
    setBusy(provider);
    social
      .token(provider)
      .then((token) => (token ? sessionApi.socialSignIn(provider, token) : null))
      .then((session) => {
        if (!session) return;
        setSession(session);
        navigate(returnTo, { replace: true });
      })
      .catch((failure: Error) => setError(errorMessage(failure)))
      .finally(() => setBusy(null));
  };

  return {
    available: { google: social.available.google && social.ready.google, facebook: social.available.facebook && social.ready.facebook },
    busy,
    error,
    choose,
  };
}
